import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";
import { IdentityVerification } from "@/components/IdentityVerification";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserRound, CheckCircle2, Store, Settings as SettingsIcon, Award } from "lucide-react";
import { toast } from "sonner";

function ProviderProfile() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [license, setLicense] = useState(user?.license || "");
  const [city, setCity] = useState(user?.city || "");
  const [zip, setZip] = useState(user?.zip || "");
  const [line1, setLine1] = useState(user?.address?.line1 || "");
  const [line2, setLine2] = useState(user?.address?.line2 || "");
  const [stateReg, setStateReg] = useState(user?.address?.state || "");
  const [logo, setLogo] = useState(user?.logo_url || "");
  const [bio, setBio] = useState(user?.bio || "");

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/badges/mine").then((r) => setBadges(r.data)).catch(() => {}); }, []);

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/profile", { license, city, zip, logo_url: logo, bio, address: { line1, line2, city, state: stateReg, zip } });
      refreshUser(data.user);
      toast.success("Business profile saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const pc = stats?.profile_completion;

  return (
    <>
      {pc && !pc.complete && (
        <div className="mt-6 rounded-sm border border-border bg-white p-6" data-testid="profile-completion">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-xl font-medium">Complete your profile</h2>
              <p className="text-sm text-muted-foreground mt-1">A complete, verified profile earns customer trust and more bookings.</p>
            </div>
            <span className="font-display text-3xl font-semibold text-primary" data-testid="profile-completion-percent">{pc.percent}%</span>
          </div>
          <Progress value={pc.percent} className="mt-4 h-2.5" data-testid="profile-completion-bar" />
          <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
            {pc.items.map((it) => (
              <li key={it.key} className={`flex items-center gap-2 text-sm ${it.done ? "text-muted-foreground" : "text-foreground"}`} data-testid={`profile-item-${it.key}`}>
                {it.done
                  ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                  : <span className="w-4 h-4 rounded-full border-2 border-muted-foreground/40 shrink-0" />}
                <span className={it.done ? "line-through" : "font-medium"}>{it.label}</span>
                {!it.done && it.key === "identity" && <span className="text-xs text-primary whitespace-nowrap">+{it.weight}% · adds Verified shield</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Business profile — location + optional license tracking */}
      <div className="mt-6 bg-white border border-border rounded-sm p-6">
        <h2 className="font-display text-xl font-medium">Business profile</h2>
        <p className="text-sm text-muted-foreground mt-1">Your business address is used to place your services on the map so nearby customers can find you.</p>
        <div className="mt-4 grid sm:grid-cols-[160px_1fr] gap-6">
          <ImageUpload value={logo} onChange={setLogo} label="Business logo" testid="logo-upload" aspect="aspect-square" />
          <div className="grid gap-3 content-start">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="line1">Street address</Label>
                <Input id="line1" value={line1} onChange={(e) => setLine1(e.target.value)} placeholder="123 Main St" className="mt-1.5 rounded-sm" data-testid="line1-input" />
              </div>
              <div>
                <Label htmlFor="line2">Suite / unit <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input id="line2" value={line2} onChange={(e) => setLine2(e.target.value)} placeholder="Suite 200" className="mt-1.5 rounded-sm" data-testid="line2-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="city">City</Label>
                <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Austin" className="mt-1.5 rounded-sm" data-testid="city-input" />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input id="state" value={stateReg} onChange={(e) => setStateReg(e.target.value)} placeholder="TX" className="mt-1.5 rounded-sm" data-testid="state-input" />
              </div>
              <div>
                <Label htmlFor="zip">ZIP</Label>
                <Input id="zip" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="78701" className="mt-1.5 rounded-sm" data-testid="zip-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="license">License / cert # <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="license" value={license} onChange={(e) => setLicense(e.target.value)} placeholder="e.g. LIC-123456" className="mt-1.5 rounded-sm max-w-xs" data-testid="license-input" />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Label htmlFor="bio">About your business <span className="text-muted-foreground font-normal">(shown on your public profile)</span></Label>
          <Textarea id="bio" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={600} rows={3}
            placeholder="Tell customers what you do, your experience, and what makes you great…"
            className="mt-1.5 rounded-sm" data-testid="bio-input" />
          <p className="text-xs text-muted-foreground mt-1">{bio.length}/600</p>
        </div>
        <Button onClick={saveProfile} className="rounded-sm mt-4" data-testid="save-license-btn">Save profile</Button>
      </div>

      <IdentityVerification />

      {/* Badges & achievements */}
      <div className="mt-6" data-testid="profile-badges-section">
        <div className="flex items-center gap-2"><Award className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-medium">Badges &amp; achievements</h2></div>
        <p className="text-sm text-muted-foreground mt-1">Share these on social media to attract more customers.</p>
        {badges.length === 0 ? (
          <div className="mt-4 border border-dashed border-border rounded-sm p-12 text-center" data-testid="badges-empty">
            <p className="text-muted-foreground">No badges yet. Earn badges by ranking top 50/100 in your categories (avg rating, min 5 completed visits) or collecting 4–5★ reviews this month.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5 mt-4">
            {badges.map((b) => {
              const img = `${process.env.REACT_APP_BACKEND_URL}${b.image_url}`;
              const shareText = `${b.caption} ${window.location.origin}/p/${user?.id}`;
              return (
                <div key={b.key} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`badge-${b.key}`}>
                  <img src={img} alt={b.title} className="w-full aspect-[1200/630] object-cover border-b border-border" data-testid={`badge-img-${b.key}`} />
                  <div className="p-4">
                    <h3 className="font-display text-lg font-semibold">{b.title}</h3>
                    <p className="text-sm text-muted-foreground">{b.subtitle}</p>
                    {b.times_reached > 0 && <p className="text-xs text-primary font-medium mt-1">Reached {b.times_reached} time{b.times_reached !== 1 ? "s" : ""}</p>}
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" className="rounded-sm h-8" onClick={() => { navigator.clipboard.writeText(shareText); toast.success("Caption copied!"); }} data-testid={`copy-caption-${b.key}`}>Copy caption</Button>
                      <a href={img} download={`${b.key}.png`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-sm h-8" data-testid={`download-badge-${b.key}`}>Download image</Button></a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

const emptyAddr = { line1: "", line2: "", city: "", state: "", zip: "" };

function AddressForm({ title, hint, value, onChange, prefix }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="bg-white border border-border rounded-sm p-6" data-testid={`${prefix}-card`}>
      <h2 className="font-display text-xl font-medium">{title}</h2>
      <p className="text-sm text-muted-foreground mt-1">{hint}</p>
      <div className="mt-4 grid gap-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`${prefix}-line1`}>Street address</Label>
            <Input id={`${prefix}-line1`} value={value.line1 || ""} onChange={(e) => set("line1", e.target.value)} placeholder="123 Main St" className="mt-1.5 rounded-sm" data-testid={`${prefix}-line1`} />
          </div>
          <div>
            <Label htmlFor={`${prefix}-line2`}>Suite / unit <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id={`${prefix}-line2`} value={value.line2 || ""} onChange={(e) => set("line2", e.target.value)} placeholder="Apt 4" className="mt-1.5 rounded-sm" data-testid={`${prefix}-line2`} />
          </div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <Label htmlFor={`${prefix}-city`}>City</Label>
            <Input id={`${prefix}-city`} value={value.city || ""} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Austin" className="mt-1.5 rounded-sm" data-testid={`${prefix}-city`} />
          </div>
          <div>
            <Label htmlFor={`${prefix}-state`}>State</Label>
            <Input id={`${prefix}-state`} value={value.state || ""} onChange={(e) => set("state", e.target.value)} placeholder="TX" className="mt-1.5 rounded-sm" data-testid={`${prefix}-state`} />
          </div>
          <div>
            <Label htmlFor={`${prefix}-zip`}>ZIP</Label>
            <Input id={`${prefix}-zip`} value={value.zip || ""} onChange={(e) => set("zip", e.target.value)} placeholder="78701" className="mt-1.5 rounded-sm" data-testid={`${prefix}-zip`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerProfileView() {
  const { user, refreshUser } = useAuth();
  const [home, setHome] = useState({ ...emptyAddr, ...(user?.address || {}) });
  const [work, setWork] = useState({ ...emptyAddr, ...(user?.address2 || {}) });
  const [saving, setSaving] = useState(false);

  const hasData = (a) => Object.values(a || {}).some((v) => (v || "").trim());

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/addresses", {
        address: hasData(home) ? home : null,
        address2: hasData(work) ? work : null,
      });
      refreshUser(data.user);
      toast.success("Addresses saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="mt-6 bg-white border border-border rounded-sm p-6" data-testid="account-info-card">
        <h2 className="font-display text-xl font-medium">Account information</h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your phone number and security in <Link to="/settings" className="text-primary underline" data-testid="account-settings-link">Settings</Link>.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input value={user?.name || ""} readOnly className="mt-1.5 rounded-sm bg-secondary/40 text-foreground" data-testid="account-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ""} readOnly className="mt-1.5 rounded-sm bg-secondary/40 text-foreground" data-testid="account-email" />
          </div>
          <div>
            <Label>Phone {user?.phone_verified && <span className="ml-1 text-xs text-green-700">verified</span>}</Label>
            <Input value={user?.phone || "Not added"} readOnly className="mt-1.5 rounded-sm bg-secondary/40 text-foreground" data-testid="account-phone" />
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6">
        <AddressForm title="Home address" hint="Your default address for services at your location." value={home} onChange={setHome} prefix="addr-home" />
        <AddressForm title="Work address" hint="An optional second address (e.g. your office)." value={work} onChange={setWork} prefix="addr-work" />
      </div>
      <Button onClick={save} disabled={saving} className="rounded-sm mt-4" data-testid="save-addresses-btn">{saving ? "Saving…" : "Save addresses"}</Button>
    </>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const isProvider = user?.role === "subscriber";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="profile-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-primary"><UserRound className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Profile</span></div>
          <h1 className="font-display text-4xl font-semibold tracking-tighter mt-2">{isProvider ? "Business profile" : "Your profile"}</h1>
          <p className="text-muted-foreground mt-2">{isProvider ? "Keep your business details current so customers can find and trust you." : "Your account details and saved addresses."}</p>
        </div>
        <div className="flex items-center gap-2">
          {isProvider && (
            <Link to={`/p/${user?.id}`}>
              <Button variant="outline" className="rounded-sm" data-testid="profile-view-public"><Store className="w-4 h-4 mr-1.5" />View public profile</Button>
            </Link>
          )}
          <Link to="/settings">
            <Button variant="outline" className="rounded-sm" data-testid="profile-settings-link"><SettingsIcon className="w-4 h-4 mr-1.5" />Settings</Button>
          </Link>
        </div>
      </div>

      {isProvider ? <ProviderProfile /> : <CustomerProfileView />}
    </div>
  );
}
